import React, { useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ScoreBadge, type GuessCell } from "./ScoreboardFeed/guess-grid";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";

const MAX_COMMENT_LENGTH = 10000;

interface StickyCommentInputBarProps {
    playablePropertyId?: string;
    solvedScore?: number | null;
    scoreBadgeGuesses?: GuessCell[][];
    onRequireSignIn: () => void;
    onSubmitComment?: (content: string) => Promise<void>;
}

export default function StickyCommentInputBar({
    playablePropertyId,
    solvedScore,
    scoreBadgeGuesses,
    onRequireSignIn,
    onSubmitComment,
}: StickyCommentInputBarProps) {
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const { isAuthenticated, user } = useAuth0();
    const userId = (userState as any)?.id ?? null;
    const isSignedIn = isAuthenticated || Boolean((userState as any)?.is_created);
    const oauthPicture = typeof user?.picture === "string" ? user.picture.trim() : "";
    const profilePhotoUrl = (userState as any)?.profilePhotoUrl ?? null;
    const avatarUrl = profilePhotoUrl || (oauthPicture ? oauthPicture : null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isScoreBadgeExpanded, setIsScoreBadgeExpanded] = useState(false);
    const [content, setContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    /** Blocks re-entrancy before `setIsSubmitting(true)` is applied (Enter / double-click). */
    const submitInFlightRef = useRef(false);
    const [error, setError] = useState<string | null>(null);

    const hasText = content.trim().length > 0;
    const canPost = hasText && content.length <= MAX_COMMENT_LENGTH && !isSubmitting && Boolean(onSubmitComment);
    const hasSolvedScore = Number.isFinite(solvedScore as number);
    const solvedGuessRows: GuessCell[][] = useMemo(() => {
        if (scoreBadgeGuesses && scoreBadgeGuesses.length > 0) {
            return scoreBadgeGuesses;
        }
        const tries = Math.max(1, Math.min(6, Number(solvedScore) || 1));
        const fallbackRow: GuessCell[] = [
            { digit: "0", status: "correct" },
            { digit: "0", status: "wrong" },
            { digit: "0", status: "close" },
            { digit: "0", status: "wrong" },
            { digit: "0", status: "correct" },
            { digit: "0", status: "wrong" },
            { digit: "0", status: "close" },
            { digit: "0", status: "wrong" },
        ];
        return Array.from({ length: tries }, () => [...fallbackRow]);
    }, [scoreBadgeGuesses, solvedScore]);

    const avatarFallback = useMemo(() => {
        return "Y";
    }, []);

    const openOrSignIn = () => {
        if (!isSignedIn) {
            onRequireSignIn();
            return;
        }
        setIsExpanded(true);
        setError(null);
    };

    const handleCancel = () => {
        setIsExpanded(false);
        setIsScoreBadgeExpanded(false);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!onSubmitComment) return;
        if (submitInFlightRef.current || isSubmitting) return;
        submitInFlightRef.current = true;
        try {
            if (!canPost) return;
            if (isSignedIn && !userId) {
                setError("Your profile is still loading. Please wait a moment and try again.");
                return;
            }
            const trimmed = content.trim();
            if (!trimmed) return;

            setError(null);
            setIsSubmitting(true);
            try {
                await onSubmitComment(trimmed);
                sendMixpanelEvent("Comment Posted", {
                    isAuthenticated: isSignedIn,
                    pageName: "Puzzle",
                    hasCommentHashtag: false,
                    comment_depth: "1st level",
                    playable_property_id: playablePropertyId ?? null,
                    comment_type: "general",
                    character_count: trimmed.length,
                    user_id: (userState as any)?.id ?? null,
                    nickname: (userState as any)?.nickname ?? user?.nickname ?? null,
                    email: (userState as any)?.emailAddress ?? user?.email ?? null,
                });
                setContent("");
                setIsExpanded(false);
                setIsScoreBadgeExpanded(false);
            } catch (error: any) {
                console.error(error);
                setError("Couldn't post your comment. Try again.");
            } finally {
                setIsSubmitting(false);
            }
        } finally {
            submitInFlightRef.current = false;
        }
    };

    return (
        <div className="tw-sticky tw-bottom-0 tw-z-30 tw-py-2 tw-px-4 tw-bg-[rgb(35,35,64)] tw-shadow-[0_-5px_10.5px_0_rgba(0,0,0,0.14)]">
            {!isExpanded ? (
                <button
                    type="button"
                    onClick={openOrSignIn}
                    className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-rounded-[22px] tw-bg-[rgb(35,35,64)] tw-border tw-border-[#2f367b] tw-px-3 tw-py-2 tw-text-left hover:tw-border-[#4a55be] tw-transition-colors"
                >
                    <div className="tw-w-[30px] tw-h-[30px] tw-rounded-full tw-overflow-hidden tw-flex-shrink-0 tw-bg-[#f2b311] tw-flex tw-items-center tw-justify-center tw-font-semibold tw-text-[#1a1d3a]">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt="Your avatar" className="tw-w-full tw-h-full tw-object-cover" />
                        ) : (
                            avatarFallback
                        )}
                    </div>
                    <div className="tw-flex-1 tw-min-w-0 tw-rounded-full tw-border tw-border-[#2f367b] tw-bg-[#111538] tw-px-4 tw-py-2.5 tw-text-[#9aa0c7] tw-text-[18px] tw-font-medium tw-leading-none">
                        What do you think of this one?
                    </div>
                </button>
            ) : (
                <>
                    <div className="tw-rounded-[22px] tw-bg-[#0f1435] tw-border tw-border-[#5a29ff] tw-p-4">
                        {hasSolvedScore && (
                            <div className="tw-mb-3">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <ScoreBadge
                                        guesses={solvedGuessRows}
                                        expanded={isScoreBadgeExpanded}
                                        onToggle={() => setIsScoreBadgeExpanded((prev) => !prev)}
                                        compact
                                        isSolved
                                    />
                                <span className="tw-text-[#9aa0c7] tw-text-sm">attached to your post</span>
                                </div>
                            </div>
                        )}

                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit();
                                }
                            }}
                            aria-label="Comment text"
                            placeholder="What do you think about this one?"
                            rows={4}
                            disabled={isSubmitting}
                            className="tw-w-full tw-bg-transparent tw-text-white tw-text-[18px] md:tw-text-[20px] tw-leading-tight tw-outline-none tw-border-none tw-resize-none placeholder:tw-text-[#8f94b2] disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                            autoFocus
                        />
                        {error && <div className="tw-text-[#ff8e8e] tw-text-sm tw-mt-2">{error}</div>}
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-3">
                        <span className="tw-text-[#8f94b2] tw-text-sm">
                            {content.length.toLocaleString()} / {MAX_COMMENT_LENGTH.toLocaleString()}
                        </span>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                                className="tw-bg-transparent tw-border-none tw-text-[#8f94b2] hover:tw-text-white tw-font-semibold tw-px-1 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!canPost}
                                className={clsx(
                                    "tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-min-w-[5.5rem] tw-rounded-2xl tw-px-5 tw-py-2 tw-font-semibold tw-transition-colors",
                                    canPost
                                        ? "tw-bg-[#F69929] tw-text-[#1a1d3a] hover:tw-bg-[#ffac48]"
                                        : "tw-bg-[#2b315f] tw-text-[#7d83a8] tw-cursor-not-allowed",
                                )}
                            >
                                {isSubmitting && (
                                    <span
                                        className="tw-inline-block tw-h-4 tw-w-4 tw-border-2 tw-border-[#1a1d3a] tw-border-t-transparent tw-rounded-full tw-animate-spin"
                                        aria-hidden
                                    />
                                )}
                                {isSubmitting ? "Posting…" : "Post"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
